import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Rocket,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Panel } from "@/components/admin/analytics/AnalyticsKit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  getSetupConfig,
  getSystemOverview,
  loadSetupDemo,
  saveSetupStep,
  updateSystemSettings,
} from "@/lib/admin/system/system.functions";
import { IMPLEMENTATION_STEPS } from "@/lib/admin/system/system.types";
import type { SetupField, SetupStep } from "@/lib/admin/system/system.types";

export const Route = createFileRoute("/_admin/admin/implementation-wizard")({
  head: () => ({
    meta: [
      { title: "Implementation Wizard — Admin" },
      {
        name: "description",
        content: "Guided first-time setup checklist for onboarding a new client onto the platform.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ImplementationWizardPage,
});

/** Demo values for one step: editable inline, badged while still demo data. */
function StepValues({
  step,
  hidden,
  onSaved,
}: {
  step: SetupStep | undefined;
  hidden: boolean;
  onSaved: () => void;
}) {
  const save = useServerFn(saveSetupStep);
  const [fields, setFields] = useState<SetupField[]>(step?.fields ?? []);

  useEffect(() => {
    setFields(step?.fields ?? []);
  }, [step]);

  const mutation = useMutation({
    mutationFn: (next: SetupField[]) =>
      save({ data: { stepKey: step!.step_key, fields: next } }),
    onSuccess: () => {
      toast.success("Saved as the client's configuration.");
      onSaved();
    },
    onError: () => toast.error("Could not save these values."),
  });

  if (!step || step.fields.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No values configured yet for this step.
      </p>
    );
  }

  if (hidden) {
    return (
      <p className="text-xs text-muted-foreground">
        Demo values are hidden while the system is in Production Mode.
      </p>
    );
  }

  const dirty = JSON.stringify(fields) !== JSON.stringify(step.fields);

  return (
    <div className="space-y-3">
      {step.notes ? <p className="text-xs text-muted-foreground">{step.notes}</p> : null}
      <div className="space-y-2">
        {fields.map((f, i) => (
          <div key={`${step.step_key}-${f.label}-${i}`} className="grid gap-1 sm:grid-cols-[220px_1fr] sm:items-center sm:gap-3">
            <span className="text-xs font-medium text-muted-foreground">{f.label}</span>
            <Input
              value={f.value}
              onChange={(e) =>
                setFields((prev) =>
                  prev.map((p, j) => (j === i ? { ...p, value: e.target.value } : p)),
                )
              }
              className="h-8 text-sm"
              aria-label={f.label}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={() => mutation.mutate(fields)}
          disabled={!dirty || mutation.isPending}
        >
          Save as client data
        </Button>
        {dirty ? (
          <Button size="sm" variant="ghost" onClick={() => setFields(step.fields)}>
            Reset
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ImplementationWizardPage() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getSystemOverview);
  const fetchSetup = useServerFn(getSetupConfig);
  const update = useServerFn(updateSystemSettings);
  const preload = useServerFn(loadSetupDemo);
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "system-overview"],
    queryFn: () => fetchOverview(),
  });

  const { data: setup } = useQuery({
    queryKey: ["admin", "setup-config"],
    queryFn: () => fetchSetup(),
  });

  const isProduction = data?.settings.system_mode === "production";
  const byKey = new Map((setup ?? []).map((s) => [s.step_key, s]));
  const demoCount = (setup ?? []).filter((s) => s.is_demo).length;

  const progress = data?.settings.implementation_progress ?? {};
  const done = IMPLEMENTATION_STEPS.filter((s) => progress[s.key]).length;
  const pctDone = Math.round((done / IMPLEMENTATION_STEPS.length) * 100);

  const mutation = useMutation({
    mutationFn: (next: Record<string, boolean>) =>
      update({ data: { implementation_progress: next } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "system-overview"] }),
    onError: () => toast.error("Progress could not be saved."),
  });

  const reload = () => qc.invalidateQueries({ queryKey: ["admin", "setup-config"] });

  const preloadMutation = useMutation({
    mutationFn: () => preload(),
    onSuccess: (res) => {
      toast.success(`Demo configuration loaded for ${res.steps} steps.`);
      reload();
    },
    onError: () => toast.error("Demo configuration could not be loaded."),
  });

  const toggle = (key: string) =>
    mutation.mutate({ ...progress, [key]: !progress[key] });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Implementation Wizard"
        description="Work through each step to take a new client from an empty system to go-live."
      />

      <Panel
        title="Setup progress"
        subtitle={`${done} of ${IMPLEMENTATION_STEPS.length} steps complete`}
      >
        <Progress value={pctDone} className="h-2" />
        <p className="mt-2 text-xs text-muted-foreground">{pctDone}% complete</p>
      </Panel>

      <Panel
        title="Demonstration configuration"
        subtitle={
          isProduction
            ? "Production Mode is on — demo values stay hidden and can be removed permanently in Production Cleanup."
            : `${demoCount} step${demoCount === 1 ? "" : "s"} still show demonstration values. Edit any value and save it to make it the client's own.`
        }
      >
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => preloadMutation.mutate()}
            disabled={preloadMutation.isPending}
          >
            <Sparkles className="h-4 w-4" aria-hidden /> Reload demo values
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link to="/admin/production-cleanup">Remove demo values permanently</Link>
          </Button>
        </div>
      </Panel>

      <Panel title="Checklist" subtitle="Tick a step once the client's real data is in place">
        <ol className="divide-y">
          {IMPLEMENTATION_STEPS.map((step, i) => {
            const complete = Boolean(progress[step.key]);
            const cfg = byKey.get(step.key);
            const expanded = open === step.key;
            return (
              <li key={step.key} className="py-3">
                <div className="flex flex-wrap items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
                    {i + 1}
                  </span>
                  <Checkbox
                    checked={complete}
                    disabled={isLoading || mutation.isPending}
                    onCheckedChange={() => toggle(step.key)}
                    className="mt-1.5"
                    aria-label={`Mark ${step.label} complete`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{step.label}</span>
                      {complete ? (
                        <Badge variant="secondary" className="gap-1 text-[10px]">
                          <CheckCircle2 className="h-3 w-3" aria-hidden /> Done
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-[10px]">
                          <Circle className="h-3 w-3" aria-hidden /> Pending
                        </Badge>
                      )}
                      {cfg?.is_demo && !isProduction ? (
                        <Badge className="text-[10px]" variant="secondary">
                          Demo Data
                        </Badge>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{step.hint}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1"
                    onClick={() => setOpen(expanded ? null : step.key)}
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                    )}
                    Values
                  </Button>
                  <Button asChild size="sm" variant="outline" className="gap-1">
                    <Link to={step.to}>
                      Configure <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                  </Button>
                </div>
                {expanded ? (
                  <div className="mt-3 rounded-md border bg-muted/30 p-3 sm:ml-10">
                    <StepValues
                      step={cfg}
                      hidden={Boolean(isProduction && cfg?.is_demo)}
                      onSaved={reload}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      </Panel>

      <Panel title="Go live" subtitle="Final steps once the checklist is complete">
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to="/admin/backup-restore">Create a backup</Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="gap-2">
            <Link to="/admin/production-cleanup">Remove demo data</Link>
          </Button>
          <Button asChild size="sm" className="gap-2" disabled={pctDone < 100}>
            <Link to="/admin/system-configuration">
              <Rocket className="h-4 w-4" aria-hidden /> Switch to Production Mode
            </Link>
          </Button>
        </div>
      </Panel>
    </div>
  );
}
